import {
  type QueryFunctionContext,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import * as R from 'remeda'

import { auth0UserAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { addProp, equalsByPath } from '~/utils'

import { useArchivedDocumentReportsInfiniteQueryKey } from './useArchivedDocumentReportsInfiniteQueryKey'

const document = graphql(`
  query DocumentReportArchiveListPage_DocumentReports(
    $brandAccountId: ID!
    $offset: Int!
  ) {
    documents(
      brandAccountId: $brandAccountId
      status: archived
      pagination: { limit: 20, offset: $offset }
    ) {
      id
      title
      writer
    }
  }
`)

type QueryKey = ReturnType<typeof useArchivedDocumentReportsInfiniteQueryKey>
type PageParam = number

export const useArchivedDocumentReportsInfiniteQuery = () => {
  const queryKey = useArchivedDocumentReportsInfiniteQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const auth0User = useAtomValue(auth0UserAtom)

  return useInfiniteQuery({
    queryFn: async ({
      pageParam: offset,
      queryKey: [_, variables],
    }: QueryFunctionContext<QueryKey, PageParam>) => {
      const result = await graphQLRequest({
        document,
        variables: { ...variables, offset },
      })
      const email = auth0User?.email ?? ''

      return R.pipe(
        result.documents ?? [],
        R.map(R.piped(addProp('isDeletable', equalsByPath('writer', email)))),
      )
    },
    queryKey,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages): PageParam | undefined =>
      0 < lastPage.length ? allPages.flat().length : undefined,
  })
}

export type DocumentReport = QueryResultOf<
  typeof useArchivedDocumentReportsInfiniteQuery
>[number]
